import { FC } from "react";
import {
  InputContainer,
  InputNumberInteger,
  InputGpsCoordinates,
  InputSelectAsync,
  InputNumberMinMax,
  InputRadioGroup,
  InputName,
  InputDescription,
  InputNumber,
} from "@components-common/forms";
import { Col, Row } from "taltech-styleguide";
import { useWindowSize } from "@hooks";
import { OrientationSelector } from "./InputOrientation";
import { useField } from "formik";
import { BuildingTypeSelector } from "../../../../bim-domain/measures-metadata/BuildingTypeSelector";
import { useWizard } from "../../wizard/context";
import { InputMeasure } from "./InputMeasure";
import { ProgressWizardStepForm } from "../../wizard/ProgressWizardStepForm";
import { isEmpty } from "ramda";
import { getConfigurationValidationSchema } from "./building-info.schema";

/**
 * Component: BuildingInformation
 *
 * This component renders a form section for entering and displaying building information.
 * It includes inputs for building name, description, type, GPS coordinates, and various height configurations.
 *
 * Props:
 * @param {Object} props - The properties for the component.
 * @param {Array} props.buildingParameters - The options for building parameters to be passed to the InputSelectAsync component.
 *
 * Returns:
 * @returns {JSX.Element} A form section for building information.
 */
export const BuildingInformation: FC<any> = ({ ...props }) => {
  const { width } = useWindowSize();
  const [field, helpers, meta] = useField("height.type");
  const [fieldHeightValue, helpersHeightValue, metaHeightValue] = useField(
    "height.value.creationType"
  );

  return (
    <Row>
      <Col xs={width > 1200 ? "6" : "12"} className="pe-4">
        <InputContainer title="Building information">
          <InputName elementClass="mb-3 required" />
          <InputDescription id="description" />
          <BuildingTypeSelector elementClass="mb-3 required" />
          <InputGpsCoordinates
            id="location.coordinates"
            label="Coordinates"
            elementClass="required"
          />
          <InputSelectAsync
            id="buildingParameters"
            label="Building parameters"
            options={props.buildingParameters}
            elementClass="required"
            source={{
              type: "REMOTE",
            }}
          />
        </InputContainer>
      </Col>
      <Col xs={width > 1200 ? "6" : "12"} className="ps-4">
        <OrientationSelector fieldId="rotation.angle.creationType" />
        <InputMeasure parentFieldId="buildingParameters" />
        <InputContainer title="Building height">
          <Row>
            <InputRadioGroup
              id="height.type"
              showLabel={false}
              options={[
                {
                  id: "floor",
                  text: "Number of floors, floor height",
                  value: "FLOOR",
                },
                {
                  id: "building",
                  text: "Building height, number of floors",
                  value: "BUILDING",
                },
              ]}
            />
          </Row>
          <Row className="mt-3">
            <h6 className="mb-2 text-capitalize">
              {field.value === "FLOOR" ? "Number Of Floors" : "Total Height"}
              <text style={{ color: "red" }}> *</text>
            </h6>
            {field.value === "FLOOR" ? 
              <InputNumberInteger
              id="numberOfLevels"
              elementClass="required"
              showLabel={false}
            />
             : 
              <InputNumber
              id="numberOfLevels"
              elementClass="required"
              showLabel={false}
              suffix=" m"
              decimalScale={3}
            />}
          </Row>
          <Row className="mt-3">
            <h6 className="mb-0 text-capitalize">
              {field.value === "FLOOR" ? "Floor Height" : "Number Of Floors"}
            </h6>
            <InputRadioGroup
              id="height.value.creationType"
              showLabel={false}
              options={[
                { id: "fixed", text: "Constant", value: "CONSTANT" },
                { id: "random", text: "Random", value: "RANDOM" },
              ]}
            />
            {fieldHeightValue.value === "RANDOM" ? (
              <InputNumberMinMax
                minId="height.value.options.min"
                maxId="height.value.options.max"
                elementClass={"d-flex"}
                minElementClass="w-50 pe-3 required"
                maxElementClass="w-50 ps-3 required"
                suffix={field.value === "FLOOR" ? " m" : ""}
                decimalScale={field.value === "FLOOR" ? 3 : 0}
              />
            ) : (
              field.value === "FLOOR" ?
              <InputNumber
                id="height.value.value"
                label={field.value === "FLOOR" ? "Height" : "Number"}
                suffix={field.value === "FLOOR" ? " m" : ""}
                elementClass="required"
                decimalScale={3}
              />
              : 
              <InputNumberInteger
                id="height.value.value"
                label={field.value === "FLOOR" ? "Height" : "Number"}
                elementClass="required"
              />
            )}
          </Row>
        </InputContainer>
      </Col>
    </Row>
  );
};

/**
 * Component: BuildingInformationForm
 *
 * This component renders the form for building information within a wizard step.
 * It initializes the form with building parameters and includes the BuildingInformation component.
 *
 * Props:
 * @param {Object} props - The properties for the component.
 * @param {string} props.title - The title of the form.
 * @param {Object} props.element - The element data for the form.
 *
 * Returns:
 * @returns {JSX.Element} A form component for building information within a wizard step.
 */
export const BuildingInformationForm: FC<any> = ({ ...props }) => {
  const wizard = useWizard();
  const { title, element } = props;
  const buildingParameters = wizard.state.buildingParameters.map(
    (option: any) => {
      return { value: option.id, text: option.name };
    }
  );

  if (isEmpty(wizard.state.step.data) && buildingParameters.length > 0) {
    element.info.buildingParameters = buildingParameters[0].value;
    element.info.buildingType = "Commercial";
  }

  return (
    <ProgressWizardStepForm
      element={element.info}
      schema={getConfigurationValidationSchema()}
      title={title}
    >
      <BuildingInformation {...props} buildingParameters={buildingParameters} />
    </ProgressWizardStepForm>
  );
};
